/**
 * FontLoadingTypes - Type definitions for font loading and management
 * 
 * Comprehensive type system for EdgeDocs font management with legal compliance
 */

export type FontWeight = 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 'normal' | 'bold';
export type FontStyle = 'normal' | 'italic' | 'oblique';
export type FontStretch = 'normal' | 'condensed' | 'expanded';

export interface FontLoadingState {
  loadedFonts: Set<string>;
  loadingFonts: Set<string>;
  failedFonts: Set<string>;
  harfbuzzReady: boolean;
  msdfAtlasesReady: boolean;
  systemCapabilities: FontSystemCapabilities;
}

export interface FontSystemCapabilities {
  hasWebFonts: boolean;
  hasVariableFonts: boolean;
  hasOpenTypeFeatures: boolean;
  hasColorFonts: boolean;
  maxFontSize: number;
  supportedFormats: string[];
}

export interface LegalDocumentFontRequirements {
  jurisdiction: 'federal' | 'california' | 'newYork' | 'texas' | 'florida' | 'international';
  documentType: 'pleading' | 'brief' | 'contract' | 'motion' | 'discovery' | 'correspondence';
  requiredFonts: string[];
  fontSize: {
    min: number;
    max: number;
    default: number;
  };
  lineSpacing: {
    min: number;
    max: number;
    default: number;
  };
  margins: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
}

export interface OpenTypeFontFeatures {
  // Ligatures
  liga: boolean;  // Standard ligatures
  clig: boolean;  // Contextual ligatures
  dlig: boolean;  // Discretionary ligatures
  hlig: boolean;  // Historical ligatures
  
  // Case
  case: boolean;  // Case-sensitive forms
  smcp: boolean;  // Small capitals
  c2sc: boolean;  // Capitals to small capitals
  pcap: boolean;  // Petite capitals
  
  // Numbers
  lnum: boolean;  // Lining figures
  onum: boolean;  // Old-style figures
  pnum: boolean;  // Proportional figures
  tnum: boolean;  // Tabular figures
  
  // Fractions
  frac: boolean;  // Fractions
  afrc: boolean;  // Alternative fractions
  
  // Kerning
  kern: boolean;  // Kerning
  
  // Stylistic
  salt: boolean;  // Stylistic alternates
  ss01: boolean;  // Stylistic set 1
  ss02: boolean;  // Stylistic set 2
  ss03: boolean;  // Stylistic set 3
}

export interface FontMetrics {
  unitsPerEm: number;
  ascender: number;
  descender: number;
  lineGap: number;
  capHeight: number;
  xHeight: number;
  averageWidth: number;
  maxWidth: number;
  weightClass: number;
  widthClass: number;
}

export interface FontSubsetConfig {
  characterSets: {
    basicLatin: string;
    extendedLatin: string;
    legalSymbols: string;
    punctuation: string;
    numbers: string;
    currency: string;
    mathematical: string;
  };
  includeKerning: boolean;
  includeLigatures: boolean;
  includeHinting: boolean;
  compressionLevel: 'none' | 'low' | 'medium' | 'high';
}

export interface FontCacheEntry {
  id: string;
  family: string;
  weight: FontWeight;
  style: FontStyle;
  data: ArrayBuffer;
  metadata: {
    version: string;
    checksum: string;
    size: number;
    format: string;
    features: string[];
    scripts: string[];
    lastAccessed: Date;
    accessCount: number;
  };
}

export interface FontLoadingProgress {
  fontFamily: string;
  stage: 'downloading' | 'parsing' | 'caching' | 'complete' | 'error';
  progress: number; // 0-100
  bytesLoaded: number;
  bytesTotal: number;
  error?: string;
}

export interface FontValidationResult {
  isValid: boolean;
  format: string;
  version: string;
  features: string[];
  scripts: string[];
  metrics: FontMetrics;
  errors: string[];
  warnings: string[];
}

// Legal-specific font configurations
export const LEGAL_FONT_STANDARDS = {
  FEDERAL_COURTS: {
    primaryFont: 'Times New Roman',
    fontSize: 12,
    lineSpacing: 2.0,
    margins: { top: 1, right: 1, bottom: 1, left: 1.5 }
  },
  CALIFORNIA_COURTS: {
    primaryFont: 'Arial',
    fontSize: 12,
    lineSpacing: 2.0,
    margins: { top: 1, right: 1, bottom: 1, left: 1.5 }
  },
  NEW_YORK_COURTS: {
    primaryFont: 'Times New Roman',
    fontSize: 12,
    lineSpacing: 2.0,
    margins: { top: 1, right: 1, bottom: 1, left: 1 }
  },
  TEXAS_COURTS: {
    primaryFont: 'Times New Roman',
    fontSize: 12,
    lineSpacing: 2.0,
    margins: { top: 1, right: 1, bottom: 1, left: 1.5 }
  },
  FLORIDA_COURTS: {
    primaryFont: 'Times New Roman',
    fontSize: 12,
    lineSpacing: 2.0,
    margins: { top: 1, right: 1, bottom: 1, left: 1.5 }
  }
} as const;

// Character sets for font subsetting
export const LEGAL_CHARACTER_SETS = {
  BASIC_LEGAL: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789.,;:!?()[]{}"\'-',
  LEGAL_SYMBOLS: '§¶©®™°′″†‡•‰‱',
  CITATIONS: '()[].,;: v.etalsupra',
  CURRENCY: '$€£¥₹₽¢₩₪₫',
  MATHEMATICAL: '±×÷≤≥≠∞∑∏∆∇∂∫√∝∈∉∪∩⊂⊃⊆⊇',
  INTERNATIONAL: 'àáâãäåæçèéêëìíîïðñòóôõöøùúûüýþÿ',
  PUNCTUATION: '.,;:!?()[]{}"\'-–—…\'\'"\"‚„‹›«»',
  FRACTIONS: '½⅓⅔¼¾⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞'
} as const;

export type LegalCharacterSet = keyof typeof LEGAL_CHARACTER_SETS;
export type LegalFontStandard = keyof typeof LEGAL_FONT_STANDARDS;
